import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import AiAskBox from '../../components/AiAskBox/AiAskBox';
import FeaturePromo from '../../components/FeaturePromo/FeaturePromo';
import { askBoxProps } from '../../data/languagePages';
import { CARDS, CARD_BY_SLUG, HUB_PATHS, cardPath, loadContent } from '../../data/cardMeanings';
import STRINGS from './strings';
import './CardMeanings.css';

const ORIGIN = 'https://nowtarot.com';
const COOKIE_NAME = 'nowtarot_cotd';
const PICK_COUNT = 12;
const CARD_BACK = '/tarot-reader/images/small/back.gif';

const DAILY_PATHS = {
  en: '/card-of-the-day/',
  es: '/es/carta-del-dia/',
  ar: '/ar/card-of-the-day/',
};

const readSavedSlug = () => {
  const match = document.cookie.match(/(?:^|; )nowtarot_cotd=([^;]*)/);
  if (!match) return null;
  try {
    const data = JSON.parse(decodeURIComponent(match[1]));
    return data?.slug && CARD_BY_SLUG[data.slug] ? data.slug : null;
  } catch {
    return null;
  }
};

const saveSlug = (slug) => {
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify({ slug }))}; path=/; max-age=31536000; SameSite=Lax`;
};

const clearSavedSlug = () => {
  document.cookie = `${COOKIE_NAME}=; path=/; max-age=0`;
};

const shuffleChoices = () => {
  const deck = [...CARDS];
  for (let i = deck.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck.slice(0, PICK_COUNT);
};

const CardOfTheDay = ({ lang = 'en' }) => {
  const strings = STRINGS[lang];
  const canonical = `${ORIGIN}${DAILY_PATHS[lang]}`;
  const [today] = useState(() => new Date());
  const [content, setContent] = useState(null);
  const [choices, setChoices] = useState([]);
  const [pickedSlug, setPickedSlug] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const saved = readSavedSlug();
    if (saved) {
      setPickedSlug(saved);
    } else {
      setChoices(shuffleChoices());
    }
    setReady(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadContent(lang).then((data) => {
      if (!cancelled) setContent(data);
    });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const card = pickedSlug ? CARD_BY_SLUG[pickedSlug] : null;
  const name = card ? card.names[lang] : '';
  const cardContent = card && content ? content[card.slug] : null;
  const dateLabel = today.toLocaleDateString(strings.localeTag, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const handlePick = (nextCard) => {
    setPickedSlug(nextCard.slug);
    saveSlug(nextCard.slug);
  };

  const handleRedraw = () => {
    clearSavedSlug();
    setPickedSlug(null);
    setChoices(shuffleChoices());
  };

  return (
    <div className="card-meanings-page" lang={lang} dir={strings.dir}>
      <Helmet>
        <html lang={lang} />
        <title>{strings.cardOfTheDayTitle}</title>
        <meta name="description" content={strings.cardOfTheDayDescription} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={`${ORIGIN}${DAILY_PATHS.en}`} />
        <link rel="alternate" hrefLang="en" href={`${ORIGIN}${DAILY_PATHS.en}`} />
        <link rel="alternate" hrefLang="es" href={`${ORIGIN}${DAILY_PATHS.es}`} />
        <link rel="alternate" hrefLang="ar" href={`${ORIGIN}${DAILY_PATHS.ar}`} />
        <meta property="og:title" content={strings.cardOfTheDayTitle} />
        <meta property="og:description" content={strings.cardOfTheDayDescription} />
        <meta property="og:image" content={card ? `${ORIGIN}${card.image}` : `${ORIGIN}/og-image.png`} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
      </Helmet>
      <Header />

      <main className="cm-container">
        <nav className="cm-breadcrumbs" aria-label="Breadcrumb">
          <Link to={strings.askPath}>{strings.breadcrumbHome}</Link>
          <span aria-hidden="true"> › </span>
          <span>{strings.cardOfTheDayHeading}</span>
        </nav>

        <header className="cm-hub-header">
          <h1>{strings.cardOfTheDayHeading}</h1>
          <p>{strings.cardOfTheDayIntro(dateLabel)}</p>
        </header>

        {ready && !card && (
          <section className="cotd-pick" aria-label={strings.cardOfTheDayPickPrompt}>
            <p className="cotd-pick-prompt">{strings.cardOfTheDayPickPrompt}</p>
            <div className="cotd-pick-grid">
              {choices.map((choice) => (
                <button
                  key={choice.slug}
                  type="button"
                  className="cotd-pick-card"
                  onClick={() => handlePick(choice)}
                  aria-label={strings.cardOfTheDayPickPrompt}
                >
                  <img src={CARD_BACK} alt="" />
                </button>
              ))}
            </div>
          </section>
        )}

        {card && (
          <section className="cm-hero">
            <img
              src={card.image}
              alt={`${name} — ${strings.arcana[card.arcana]}`}
              className="cm-hero-image"
              width="300"
              height="527"
            />
            <div className="cm-hero-text">
              <p className="cm-arcana-label">{strings.todaysCard}</p>
              <h2>{name}</h2>
              <div className="cm-verdicts">
                <span className={`cm-verdict cm-verdict-${card.polarity.upright}`}>
                  {strings.upright}: {strings.polarity[card.polarity.upright]}
                </span>
              </div>
              {cardContent && (
                <>
                  <p>
                    <strong>{strings.uprightKeywords}:</strong>{' '}
                    {cardContent.keywords.upright.join(', ')}
                  </p>
                  <p>{cardContent.upright.general.split('\n\n')[0]}</p>
                </>
              )}
              <p>
                <Link to={cardPath(card, lang)} className="cm-inline-link">
                  {strings.readFullMeaning(name)} →
                </Link>
              </p>
              <button type="button" className="cotd-redraw" onClick={handleRedraw}>
                {strings.drawNewCardOfTheDay}
              </button>
            </div>
          </section>
        )}

        <section className="cm-cta cm-cta-dark">
          <h2>{name ? strings.askCtaHeadingForCard(name) : strings.askCtaHeading}</h2>
          <p>{strings.askCtaText}</p>
          <AiAskBox {...askBoxProps(lang)} />
          <p>
            <Link to={HUB_PATHS[lang]} className="cm-inline-link">
              {strings.allCards} →
            </Link>
          </p>
        </section>

        <FeaturePromo variant="page" lang={lang} seed="card-of-the-day" exclude="/card-of-the-day/" />
      </main>

      <Footer />
    </div>
  );
};

export { DAILY_PATHS };
export default CardOfTheDay;
